<script>
import {
  CdrButton, IconArrowLeft, IconArrowRight,
} from '@rei/cedar';
import { LandingContentBlock } from '@rei/landing-content-block';
import DotComponent from './DotComponent.vue';
import SlideComponent from './SlideComponent.vue';
import ProductTile from './ProductTile.vue';

/*
*
* this entire Filmstrip component (and it's children)
* was copied from the landing-site repo
* https://gitlab.com/rei/internal/product-engineering/LAN/landing-filmstrip-carousel
* We've added a couple text features to it:
* The SlideComponent.vue's `heading` and `price` props, as well as
* the green banner highlighting the member reward amount
*/
export default {
  name: 'FilmstripCarousel',
  components: {
    CdrButton,
    IconArrowLeft,
    IconArrowRight,
    DotComponent,
    SlideComponent,
    LandingContentBlock,
    ProductTile,
  },
  props: {
    slides: { type: Array, required: true, default: () => [] },
    slideStyle: { type: String, required: false, default: 'Lifestyle 3:4 Small' },
    content: { type: Object, required: false, default: undefined },
    slidesVisible: { type: Number, required: false, default: 3 },
    prefixAriaLabel: { type: String, required: false, default: 'slide' },
    filmstripId: { type: String, required: false, default: 'filmstrip-1' },
    overflow: { type: Boolean, required: false, default: false },
  },
  data() {
    return {
      trackLeft: 0,
      currentSlideIndex: 0,
      currentPaginationDot: 1,
      horizontalTranslate: 0,
      mobileSlideWidth: 0,
      windowWidth: 0,
      maxRight: 0,
    };
  },
  computed: {
    arrowsEnabled() {
      return {
        left: this.currentSlideIndex > 0,
        right: this.currentSlideIndex + this.slidesVisible < this.slides.length,
      };
    },
    mapDots() {
      return this.slides.map((slide, index) => ({
        index: index + 1,
        isActive: ((index + 1) === this.currentPaginationDot),
      }));
    },
    isProductSquare() {
      return this.slideStyle === 'Product 1:1';
    },
  },
  mounted() {
    this.onResize();
    window.addEventListener('resize', this.onResize);
  },
  /* c8 ignore start */
  beforeUnmount() {
    window.removeEventListener('resize', this.onResize);
  },
  /* c8 ignore end */
  methods: {
    getSlideWidth() {
      if (!this.slides.length > 0 && !this.$refs['slide-0']) return;
      this.mobileSlideWidth = this.$refs['slide-0'][0]?.clientWidth;
    },
    onResize() {
      if (this.windowWidth !== window.innerWidth) {
        // Reset scrollings on resize

        const scrollWidth = this.$refs['container-slider']?.scrollWidth;
        const scrollOffsetWidth = this.$refs['container-slider']?.offsetWidth;
        this.maxRight = scrollWidth - scrollOffsetWidth;

        this.trackLeft = this.$refs.track?.getBoundingClientRect().left;
        this.scrollToDotItem(0);
        this.currentPaginationDot = 1;
        this.horizontalTranslate = 0;
        this.currentSlideIndex = 0;
        this.getSlideWidth();
        // Storing window width to about edge cases with event listener resize
        this.windowWidth = window.innerWidth;
      }
    },
    getTabIndex(index) {
      // Make sure non-visible slides are not focusable and visible slides are.
      const rangeStart = this.currentSlideIndex;
      const rangeEnd = this.currentSlideIndex + this.slidesVisible - 1;
      return index >= rangeStart && index <= rangeEnd ? 0 : -1;
    },
    scrollTheSlides(direction) {
      const {
        currentSlideIndex, slides, slidesVisible, trackLeft,
      } = this;

      // Cache some things
      const totalSlides = slides?.length || 0;
      const lastPossibleIndex = totalSlides - slidesVisible;
      const lastVisibleIndex = currentSlideIndex + slidesVisible;
      const hasEnoughOverflowToScrollRight = lastVisibleIndex < totalSlides - slidesVisible + 1;
      const hasEnoughOverflowToScrollLeft = currentSlideIndex - slidesVisible + 1;

      // We will be setting values on these variables.
      let scrollDistance;
      let nextSlideIndex;
      let nextSlide;
      let nextFocusable;

      if (direction === 'right') {
        // 1. Get the next slide index
        nextSlideIndex = hasEnoughOverflowToScrollRight ? lastVisibleIndex - 1 : lastPossibleIndex;

        // 2. Determine how far we need to scroll to the next slide
        [nextSlide] = this.$refs[`slide-${nextSlideIndex}`];
        scrollDistance = (
          Math.min((nextSlide.getBoundingClientRect().left - trackLeft), this.maxRight)
        ) * -1;

        // 3. Determine which slide link should be focused.
        if (hasEnoughOverflowToScrollRight) {
          nextFocusable = nextSlide?.querySelector('[data-focus]') || null;
        } else {
          const [nextFocusableSlide] = this.$refs[`slide-${lastVisibleIndex - 1}`];
          nextFocusable = nextFocusableSlide?.querySelector('[data-focus]') || null;
        }
      } else if (direction === 'left') {
        // 1. Get the next slide index.
        nextSlideIndex = hasEnoughOverflowToScrollLeft > 0
          ? currentSlideIndex - slidesVisible + 1 : 0;

        // 2. Determine how far we need to scroll to the next slide
        [nextSlide] = this.$refs[`slide-${nextSlideIndex}`];
        scrollDistance = nextSlide.getBoundingClientRect().left * -1 + trackLeft;

        // 3. Determine which slide link should be focused.
        const [nextFocusableSlide] = this.$refs[`slide-${nextSlideIndex + slidesVisible - 1}`];
        nextFocusable = nextFocusableSlide?.querySelector('[data-focus]') || null;
      }
      // 4. Update state: the current index now set to the next index.
      this.currentSlideIndex = nextSlideIndex;
      // 5. Update the translate value on the carousel track.
      // Min check prevents content from scrolling too far to the right when scrolling rapidly.
      this.horizontalTranslate = Math.min(this.horizontalTranslate + scrollDistance, 0);
      // if (this.horizontalTranslate > 0) this.horizontalTranslate = 0;
      // 6. Set focus on the next focusable link.
      if (nextFocusable) nextFocusable.focus({ preventScroll: true });
    },
    scrollToDotItem(index) {
      if (!this.$refs['container-slider']) return;
      this.$refs['container-slider']?.scrollTo({
        left: (index - 1) * this.mobileSlideWidth,
        behavior: 'smooth',
      });
    },
    getContainerScroll() {
      // Only mobile
      // 1. Get the left pixels space of the slide container when it get scroll
      // and get maximum scroll width and scroll offset
      const scrollWidth = this.$refs['container-slider']?.scrollWidth;
      const scrollLeft = this.$refs['container-slider']?.scrollLeft;
      const scrollOffsetWidth = this.$refs['container-slider']?.offsetWidth;

      // 2.Check if the scroll is on the limit, this means that is on the last dot
      // this logic is to prevent when 2 item are on the view port and we could
      // identify the last item dot
      if ((scrollLeft + scrollOffsetWidth) === scrollWidth) {
        this.currentPaginationDot = this.slides.length;
      } else {
        // 3. Divide scroll left over the width of the element to get how many items
        // has scrolled. We need round the result and add 1 to set the current pagination
        // dot.
        const itemView = Math.round(scrollLeft / this.mobileSlideWidth) + 1;
        this.currentPaginationDot = itemView;
      }
    },
  },
};
</script>
<template>
  <div
    class="filmstrip-carousel"
    :class="[{
      'filmstrip-carousel--with-overflow': !!overflow,
      'filmstrip-carousel--without-overflow': !overflow,
      'filmstrip-carousel--has-content': !!content
    }]"
  >
    <div
      v-if="content"
      class="filmstrip-carousel__content"
    >
      <landing-content-block v-bind="content" />
    </div>

    <slot name="above-carousel" />

    <div class="filmstrip-carousel__carousel">
      <cdr-button
        :disabled="!arrowsEnabled.left"
        class="filmstrip-carousel__arrow filmstrip-carousel__arrow--left"
        aria-label="Previous slides"
        icon-only
        with-background
        data-analytics-id="Previous slides"
        @click="scrollTheSlides('left')"
      >
        <icon-arrow-left />
      </cdr-button>
      <div
        ref="container-slider"
        class="filmstrip-carousel__container"
        @scroll="getContainerScroll"
      >
        <ul
          ref="track"
          :style="`transform: translateX(${horizontalTranslate}px)`"
          class="filmstrip-carousel__track"
        >
          <!-- https://eslint.vuejs.org/rules/no-v-for-template-key-on-child.html -->
          <template
            v-for="({ props }, index) in slides"
            :key="index"
          >
            <li
              :ref="`slide-${index}`"
              class="filmstrip-carousel__slide"
              :class="[
                { 'lifestyle-portrait-sm': 'Lifestyle 3:4 Small' === slideStyle },
                { 'last-slide': index === slides.length - 1 }
              ]"
            >
              <product-tile
                v-if="isProductSquare"
                v-bind="{
                  lastSlide: index === slides.length - 1,
                  ...props
                }"
              />
              <slide-component
                v-else
                v-bind="{
                  slideStyle,
                  dynamicTabIndex: getTabIndex(index),
                  lastSlide: index === slides.length - 1,
                  ...props,
                }"
              />
            </li>
          </template>
        </ul>
      </div>
      <cdr-button
        :disabled="!arrowsEnabled.right"
        class="filmstrip-carousel__arrow filmstrip-carousel__arrow--right"
        aria-label="Next slides"
        icon-only
        with-background
        data-analytics-id="Next slides"
        @click="scrollTheSlides('right')"
      >
        <icon-arrow-right />
      </cdr-button>
      <div class="filmstrip-carousel__pagination-container">
        <dot-component
          v-for="dot in mapDots"
          :key="`dot-${dot.index}`"
          :filmstrip-id="filmstripId"
          :index="dot.index"
          :is-active="dot.isActive"
          :slides-length="slides.length"
          :prefix-aria-label="prefixAriaLabel"
          @click="scrollToDotItem(dot.index)"
        />
      </div>
    </div>
  </div>
</template>
<style lang="scss">
@import "../../../../style/filmstripMixins.scss";
@import "@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss";

.filmstrip-carousel {
  $viewport: 100vw;
  $container: $cdr-space-one-x;
  $cdrContainer: calc((#{$viewport} - 123.2rem) / 2 + #{$cdr-space-two-x});
  $arrow: 40px;
  margin-top: $cdr-space-two-x;
  @include cdr-sm-mq-up {
    margin-top: $cdr-space-three-x;
  }

  &__carousel {
    position: relative;
  }

  &__slide {
    list-style: none;
    display: flex;

    @include cdr-sm-mq-down {
      // Note: Scroll snap align makes that the item ignores any
      // parent spacing, in this case is ignoring the parent's inner
      // padding. It requires that the child sets itself a padding to
      // respect space between.
      scroll-snap-align: start;
      padding-right: $cdr-space-one-x;

      &.lifestyle-portrait-sm {
        padding-right: $cdr-space-three-quarter-x;
      }

      &.last-slide {
        padding-right: 0;
      }
    }
  }

  &__arrow {
    display: none;
    margin: 0;

    @include cdr-sm-mq-up {
      display: block;
      position: absolute;
      height: $arrow;
      width: $arrow;
      top: calc($cdr-space-one-x * -1);
      transform: translateY(-100%);
    }
  }

  &--without-overflow &__arrow {
    @include cdr-sm-mq-up {
      &--right {
        right: 0;
      }

      &--left {
        right: calc((#{$arrow} + (#{$cdr-space-one-and-a-half-x})));
      }
    }
  }

  &--with-overflow &__arrow {
    @include cdr-sm-mq-up {
      &--right {
        right: calc((#{$arrow} - #{$cdr-space-two-x}));
      }
      &--left {
        right: calc((#{$arrow} + (#{$cdr-space-one-and-a-half-x})));
      }
    }

    @include cdr-md-mq-up {
      &--right {
        right: calc((#{$arrow} - #{$cdr-space-half-x}));
      }
      &--left {
        right: calc((#{$arrow} + (#{$cdr-space-three-x})));
      }
    }
  }

  &__container {
    @include scroll-without-scrollbars;
    padding-bottom: 2px; /* prevent focus ring from being cut off */
  }

  &--has-content &__container {
    margin-top: $cdr-space-one-x;
  }

  .ls-content-block__subheading.ls-content-block__subheading {
    @include cdr-text-body-300;
    text-wrap: wrap;
  }

  &--with-overflow &__container {
    overflow: scroll;

    @include cdr-sm-mq-down {
      width: calc($viewport - $cdr-space-one-x);
    }

    @include cdr-lg-mq-only {
      width: calc($viewport - $cdrContainer);
    }
  }

  &__track {
    display: flex;
    transition: transform 0.6s cubic-bezier(0.15, 0, 0.15, 1);
    padding: 0;
    margin: 0;
  }

  &__pagination-container {
    display: none;

    @include cdr-sm-mq-down {
      display: flex;
      justify-content: center;
      width: 100%;
      height: auto;
      padding-top: $cdr-space-two-x;
      padding-bottom: $cdr-space-eighth-x;
    }
  }
}

</style>
